// Yandex.Metrika SPA route hits.
//
// The counter snippet in `index.html` (loaded from <head> before the app
// bundle, gated on the production hostname) sends the initial pageview with
// `url: location.href`. This hook reports `ym(COUNTER_ID, 'hit', url)` for
// every subsequent SPA navigation (pathname + search; hash-only changes are
// ignored) so virtual pageviews land in Metrika reports.
//
// Why module-level state instead of a `useRef`?
//   - React StrictMode double-invokes effects in dev: a per-instance ref
//     would fire a spurious hit on the second invocation.
//   - Remounts of the shell (e.g. auth pages -> app layout) must not
//     re-report the URL the snippet already counted; comparing against the
//     URL the page loaded with covers both cases.

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const METRIKA_COUNTER_ID = 113266304;

/**
 * Metrika tracks the production host only: `preview-*.aigentos.tech`,
 * localhost and any other host must stay silent (the counter script itself
 * produced ERR_CERT noise when it loaded off-prod). The `index.html` snippet
 * gates its loader/init on the same hostname, so `window.ym` never exists
 * off-prod; this explicit check keeps the SPA-hit side a self-documenting
 * no-op instead of relying on the missing global alone.
 */
const METRIKA_PROD_HOST = 'app.aigentos.tech';

declare global {
  interface Window {
    /** Queue API injected by the snippet; absent when the counter is blocked. */
    ym?: (...args: unknown[]) => void;
  }
}

// The URL the page loaded with — the snippet already reported it as the
// first pageview, so it is the baseline for change detection.
const loadedUrl: string | null =
  typeof window === 'undefined' ? null : window.location.pathname + window.location.search;
let lastTrackedUrl: string | null = loadedUrl;

/**
 * Reports a Yandex.Metrika hit on every SPA route change (pathname + search).
 * Silently no-ops when the counter is unavailable (adblock / privacy mode)
 * or when the page is served off the production host.
 */
export function useMetrikaRouteHits(): void {
  const location = useLocation();

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.location.hostname !== METRIKA_PROD_HOST) return;
    const url = `${location.pathname}${location.search}`;
    if (url === lastTrackedUrl) return;
    lastTrackedUrl = url;

    // Guard: adblock or strict privacy extensions remove `window.ym` —
    // analytics must never break the app.
    if (typeof window.ym === 'function') {
      window.ym(METRIKA_COUNTER_ID, 'hit', url);
    }
  }, [location.pathname, location.search]);
}
